/**
 * Bigint wire-format helpers — Faz 8.1 (ADR-061 §Karar 12).
 *
 * The replay-server stringifies bigints via `Codec.stringifyJsonBigint`
 * (kernel `Events.bigintReplacer`). The UI receives them as decimal
 * strings and converts back to `bigint` on a per-field allowlist —
 * the field name set is intentionally narrow because some legitimate
 * digit-only strings (e.g. `correlationId="12345"`) MUST stay strings.
 *
 * Kernel envelope-level bigint fields: `tickId`, `ts`. Payload-level
 * bigint fields appear in many event kinds (`price`, `volume`,
 * `usdMicros`, …) — for the UI we keep them as strings because
 * rendering and copy-paste both prefer the string form. If a future
 * view needs arithmetic on a bigint payload field, it can call
 * `toBigIntField(value)` ad-hoc.
 */

const ENVELOPE_BIGINT_KEYS = new Set(["tickId", "ts"]);

/** Convert a decimal string to bigint; throws on non-bigint shape. */
export const toBigIntField = (s: string): bigint => BigInt(s);

/**
 * Recursively walks a parsed JSON value and converts envelope-level
 * bigint fields. Pure; returns a new object/array tree.
 */
export const reviveEnvelopeBigints = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(reviveEnvelopeBigints) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (ENVELOPE_BIGINT_KEYS.has(k) && typeof v === "string") {
        try {
          out[k] = BigInt(v);
        } catch {
          out[k] = v;
        }
      } else {
        out[k] = reviveEnvelopeBigints(v);
      }
    }
    return out as unknown as T;
  }
  return value;
};
